import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { PoiStatus } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { upperSnakeCase } from '@/utils';

/**
 * Default labels for POI status values.
 * These are used when no custom labels are configured at the structure level.
 */
const DEFAULT_POI_STATUS_LABELS: Record<string, string> = {
  [PoiStatus.Inprogress]: 'In Progress',
  [PoiStatus.Completed]: 'Completed',
  [PoiStatus.Inreview]: 'In Review',
  [PoiStatus.ChaseItemAdded]: 'Chase Item Added',
  [PoiStatus.PartiallyCompleted]: 'Partially Completed',
  [PoiStatus.Remediated]: 'Remediated',
};

/**
 * Maps PoiStatus enum string values (e.g. 'INPROGRESS') to the camelCase keys
 * used in StatusConfig (e.g. 'inprogress').
 */
const POI_STATUS_TO_CONFIG_KEY: Record<string, keyof StatusConfigFields> = {
  [PoiStatus.Inprogress]: 'inprogress',
  [PoiStatus.Completed]: 'completed',
  [PoiStatus.Inreview]: 'inreview',
  [PoiStatus.ChaseItemAdded]: 'chaseItemAdded',
  [PoiStatus.PartiallyCompleted]: 'partiallyCompleted',
  [PoiStatus.Remediated]: 'remediated',
};

/**
 * Reverse of POI_STATUS_TO_CONFIG_KEY — maps camelCase config keys (e.g.
 * 'partiallyCompleted') back to the canonical PoiStatus value (e.g.
 * 'PARTIALLY_COMPLETED') so that meta items stored with either format resolve
 * to the same key in the labels map and don't create duplicate entries.
 */
const CONFIG_KEY_TO_POI_STATUS: Record<string, string> = Object.fromEntries(
  Object.entries(POI_STATUS_TO_CONFIG_KEY).map(([statusValue, configKey]) => [
    configKey,
    statusValue,
  ])
);

type StatusConfigFields = {
  inprogress?: { name?: string | null; hidden?: boolean | null } | null;
  completed?: { name?: string | null; hidden?: boolean | null } | null;
  inreview?: { name?: string | null; hidden?: boolean | null } | null;
  chaseItemAdded?: { name?: string | null; hidden?: boolean | null } | null;
  partiallyCompleted?: { name?: string | null; hidden?: boolean | null } | null;
  remediated?: { name?: string | null; hidden?: boolean | null } | null;
};

/**
 * Hook to get dynamic POI status labels.
 *
 * Deployment-level labels from the Settings collection are applied to `labels`
 * and `statusOptions`. For per-template overrides, pass an optional `type` to
 * `getPoiStatusLabel` — it will check the matching template's `statusConfig`
 * first and fall back to the deployment-level label if none is configured.
 *
 * @returns Object with:
 * - `getPoiStatusLabel`: Function to get the display label for a given status,
 *   with an optional `type` to apply per-template overrides
 * - `labels`: Record mapping each PoiStatus to its deployment-level display label
 * - `statusOptions`: Array of { value, label } for use in dropdowns
 */
export const usePoiStatusLabels = () => {
  const poiStatusLabels = useAtomValue(state.poiStatusLabels);
  const allPoiTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const labels = useMemo(() => {
    const result: Record<string, string> = {};

    for (const [key, value] of Object.entries(DEFAULT_POI_STATUS_LABELS)) {
      result[key] = value;
    }

    if (poiStatusLabels?.meta) {
      for (const metaItem of poiStatusLabels.meta) {
        if (metaItem.__typename === 'StringMeta') {
          const normalizedKey = CONFIG_KEY_TO_POI_STATUS[metaItem.key] ?? metaItem.key;
          result[normalizedKey] = metaItem.value;
        }
      }
    }
    return result;
  }, [poiStatusLabels]);

  const getStatusLabelMap = useCallback(
    (type?: string): Record<string, string> => {
      if (type !== undefined) {
        const matchedTemplate = allPoiTemplates.find(
          (template) => upperSnakeCase(template.key) === upperSnakeCase(type)
        );
        const statusConfig = matchedTemplate?.statusConfig as StatusConfigFields | null | undefined;

        if (statusConfig) {
          const result = { ...labels };
          for (const [statusValue, configKey] of Object.entries(POI_STATUS_TO_CONFIG_KEY)) {
            const customName = statusConfig[configKey]?.name;
            if (customName) result[statusValue] = customName;
          }
          return result;
        }
      }

      return labels;
    },
    [labels, allPoiTemplates]
  );

  const getPoiStatusLabel = useCallback(
    (status: string, type?: string): string => {
      if (type !== undefined) {
        const matchedTemplate = allPoiTemplates.find(
          (template) => upperSnakeCase(template.key) === upperSnakeCase(type)
        );
        const statusConfig = matchedTemplate?.statusConfig as StatusConfigFields | null | undefined;
        const configKey = POI_STATUS_TO_CONFIG_KEY[status];

        if (statusConfig && configKey) {
          const customName = statusConfig[configKey]?.name;
          if (customName) return customName;
        }
      }

      return labels[status] ?? status;
    },
    [labels, allPoiTemplates]
  );

  const getStatusOptions = useCallback(
    (type?: string) => {
      if (type !== undefined) {
        const matchedTemplate = allPoiTemplates.find(
          (template) => upperSnakeCase(template.key) === upperSnakeCase(type)
        );
        const statusConfig = matchedTemplate?.statusConfig as StatusConfigFields | null | undefined;

        if (statusConfig) {
          return Object.entries(labels)
            .filter(([statusValue]) => {
              const configKey = POI_STATUS_TO_CONFIG_KEY[statusValue];
              if (!configKey) return true;
              return statusConfig[configKey]?.hidden !== true;
            })
            .map(([key]) => ({
              value: key,
              label: getPoiStatusLabel(key, type),
            }));
        }
      }

      return Object.entries(labels).map(([key, value]) => ({
        value: key,
        label: value,
      }));
    },
    [labels, allPoiTemplates, getPoiStatusLabel]
  );

  const statusOptions = useMemo(() => getStatusOptions(), [getStatusOptions]);
  return {
    getPoiStatusLabel,
    getStatusOptions,
    getStatusLabelMap,
    labels,
    statusOptions,
  };
};

export { DEFAULT_POI_STATUS_LABELS };
